<script setup lang="ts">
// The registry side of the seam (§8/§10). Everything legacy reached for by import —
// `PerformanceOverviewArea`, the scorecard widget, `SummaryInsights`, `SummaryAnomalies`,
// `FunnelExploration`, `Alerts` — arrives here as a loader some other feature pushed in, so this
// file names none of them.
//
// The unavailable placeholder is the point, not a nicety: a kind is unregistered whenever its
// feature is unmigrated, disabled for the workspace, or absent from this build, and legacy turned
// that into a blank block or a crash that took the whole dashboard with it (§14).
import { computed, defineAsyncComponent, type Component } from 'vue'

import { resolveDashboardWidget, type DashboardWidgetEmbedProps } from '../../flows/widgetRegistry'
import WidgetSkeleton from '../skeletons/WidgetSkeleton.vue'

import WidgetUnavailable from './WidgetUnavailable.vue'

const props = defineProps<DashboardWidgetEmbedProps>()
const emit = defineEmits<{ 'has-insights-change': [visible: boolean] }>()

/**
 * Resolved per `kind`. `defineAsyncComponent` also absorbs a failed chunk fetch — `errorComponent`
 * degrades this one block to the same placeholder rather than letting the rejection reach the grid.
 */
const resolved = computed<Component | null>(() => {
  const loader = resolveDashboardWidget(props.widget.kind)
  if (!loader) return null
  return defineAsyncComponent({
    loader,
    loadingComponent: WidgetSkeleton,
    errorComponent: WidgetUnavailable,
    delay: 120,
  })
})
</script>

<template>
  <component
    :is="resolved"
    v-if="resolved"
    :widget="widget"
    :title="title"
    :ws-id="wsId"
    :account-workspaces="accountWorkspaces"
    :read-only="readOnly"
    @has-insights-change="emit('has-insights-change', $event)"
  />
  <WidgetUnavailable v-else />
</template>
